/**
 * Week-1 ROI Report status card — top of /admin/conversion-roi.
 *
 * Shows: "no pipeline" / "waiting" / "eligible now" / "sent" states + a
 * manual trigger button. The full automation runs on a 4h cron — this card
 * is the user-visible affordance.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Mail, Clock, CheckCircle2, AlertTriangle, FileDown, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_META = {
  no_pipeline_connected: {
    label: 'Connect a pipeline first',
    Icon: AlertTriangle,
    cls: 'bg-slate-800 text-slate-200 border-slate-600/40',
    headline: 'Connect PostHog, Mixpanel, Amplitude, or Segment first — we need 30 days of events to compute the ROI report.',
  },
  waiting_for_window: {
    label: 'Backfilling — report drops within 24-72h',
    Icon: Clock,
    cls: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40',
    headline: 'Your pipeline is connected. We wait 24-72h for events to flow, then auto-email the PDF.',
  },
  eligible_now: {
    label: 'Eligible right now',
    Icon: Sparkles,
    cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    headline: 'Your data is ready. Auto-dispatch will fire within 4 hours, or click below to trigger immediately.',
  },
  sent: {
    label: 'Report sent',
    Icon: CheckCircle2,
    cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    headline: 'Your week-1 ROI PDF was emailed. Click "Resend" to get another copy.',
  },
  window_expired: {
    label: 'Window expired',
    Icon: AlertTriangle,
    cls: 'bg-amber-500/20 text-amber-200 border-amber-400/40',
    headline: 'The auto-dispatch window passed without an email. Trigger manually below.',
  },
};

export default function Week1RoiCard() {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/week1-roi/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/week1-roi/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) setTriggerResult({ ok: true, ...d });
      else setTriggerResult({ ok: false, error: d.detail || `HTTP ${r.status}` });
      refresh();
    } finally { setTriggering(false); }
  };

  if (!status) {
    return <Card className="bg-slate-950 border-emerald-500/20 animate-pulse" data-testid="week1-loading"><CardContent className="p-6 h-32" /></Card>;
  }

  const meta = STATE_META[status.state] || STATE_META.no_pipeline_connected;
  const Icon = meta.Icon;
  const canTrigger = status.state === 'eligible_now' || status.state === 'window_expired' || status.state === 'sent';

  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="week1-roi-card">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-400" />
          Week-1 ROI Report
          <Badge className={`${meta.cls} border text-[9px] font-mono tracking-widest ml-1`}>{meta.label.toUpperCase()}</Badge>
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">An auto-generated PDF emailed to you 24-72h after your first analytics-pipeline connect — "Here are the features bleeding money this month, in priority order."</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3 rounded-md bg-slate-900 border border-emerald-500/20 p-3">
          <Icon className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-slate-100">{meta.headline}</div>
        </div>

        {status.first_pipeline_connection_at && (
          <div className="text-xs text-slate-300 space-y-0.5">
            <div>First pipeline connected: <span className="text-emerald-300 font-mono">{new Date(status.first_pipeline_connection_at).toLocaleString()}</span></div>
            {status.dispatch?.sent_at && (
              <div>Report sent: <span className="text-emerald-300 font-mono">{new Date(status.dispatch.sent_at).toLocaleString()}</span> to <code className="text-emerald-300">{status.dispatch.to_email}</code></div>
            )}
            {status.dispatch?.losing_feature_count !== undefined && (
              <div>Findings: <span className="text-amber-300 font-bold">{status.dispatch.losing_feature_count}</span> loss-making features · total bleed <span className="text-amber-300 font-bold">${status.dispatch.total_loss_usd?.toFixed(2)}</span></div>
            )}
          </div>
        )}

        {canTrigger && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button onClick={trigger} disabled={triggering} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="week1-trigger-btn">
              <FileDown className="w-4 h-4 mr-1.5" />
              {triggering ? 'Generating PDF + sending…' : status.state === 'sent' ? 'Resend report' : 'Send Week-1 ROI now'}
            </Button>
            {triggerResult && (
              triggerResult.ok ? (
                <div className="text-xs text-emerald-300">
                  ✓ {triggerResult.losing_feature_count} loss-makers · ${triggerResult.total_loss_usd?.toFixed(2)} bleed · PDF {(triggerResult.pdf_size_bytes / 1024).toFixed(1)}kb · {triggerResult.send_result?.sent ? 'emailed' : `email skipped (${triggerResult.send_result?.reason})`}
                </div>
              ) : (
                <div className="text-xs text-rose-300">{triggerResult.error}</div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
